/**
 * Copyright (c) 2026 Farelanes LLC. All rights reserved.
 *
 * Client log levels: silent, error, warn, info, debug, verbose.
 */

const LEVELS = {
    silent: 0,
    error: 1,
    warn: 2,
    info: 3,
    debug: 4,
    verbose: 5,
};

function readConfiguredLevelName() {
    if (typeof window !== 'undefined') {
        try {
            const stored = window.localStorage.getItem('farelanes.logLevel');
            if (stored && stored.toLowerCase() in LEVELS) {
                return stored.toLowerCase();
            }
        } catch {
            // Private mode can throw on localStorage.
        }
        if (window.__FARELANES_LOG_LEVEL && String(window.__FARELANES_LOG_LEVEL).toLowerCase() in LEVELS) {
            return String(window.__FARELANES_LOG_LEVEL).toLowerCase();
        }
    }
    const fromEnv = String(process.env.REACT_APP_LOG_LEVEL || 'info').toLowerCase();
    return fromEnv in LEVELS ? fromEnv : 'info';
}

function currentLevel() {
    return LEVELS[readConfiguredLevelName()];
}

function emit(minLevel, consoleMethod, args) {
    if (currentLevel() < minLevel) {
        return;
    }
    consoleMethod(...args);
}

export function getLogLevel() {
    return readConfiguredLevelName();
}

/** Persist a level for this browser. Reload is not required. */
export function setLogLevel(level) {
    const name = String(level || '').toLowerCase();
    if (!(name in LEVELS)) {
        return getLogLevel();
    }
    if (typeof window !== 'undefined') {
        try {
            window.localStorage.setItem('farelanes.logLevel', name);
        } catch {
            window.__FARELANES_LOG_LEVEL = name;
        }
    }
    return name;
}

export const logger = {
    error: (...args) => emit(LEVELS.error, console.error, args),
    warn: (...args) => emit(LEVELS.warn, console.warn, args),
    info: (...args) => emit(LEVELS.info, console.info, args),
    debug: (...args) => emit(LEVELS.debug, console.debug, args),
    verbose: (...args) => emit(LEVELS.verbose, console.debug, args),
};
